import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Paper,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { DatePicker, StaticDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import 'dayjs/locale/pt-br';
import ErrorBox from '../../../components/shared/boxes/ErrorBox';

const DataFilter = (props) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const anchorRef = useRef(null);
  const { clearFilter } = props;

  const handleOpen = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setOpen(true);
  };

  const handleCancel = () => {
    if (invalidDate) {
      setStartDate(null)
      setEndDate(null)
    }

    setTempStartDate(null);
    setTempEndDate(null);
    setOpen(false);
  };

  const handleConfirm = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setOpen(false);
    const formattedRangeTime = `${tempStartDate.format('YYYY/MM/DD')} - ${tempEndDate.format('YYYY/MM/DD')}`;
    props.onChange?.(formattedRangeTime);
  };

  const invalidDate = tempStartDate && tempEndDate && (tempEndDate - tempStartDate) < 0;

  useEffect(() => {
    if (clearFilter) {
      handleCancel();
      setStartDate(null);
      setEndDate(null);
    }
  }, [clearFilter]);

  const displayValue =
    startDate && endDate ? `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}` : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Box>
        <FormControl sx={{ minWidth: '16rem' }}>
          <TextField
            readOnly
            value={displayValue}
            onClick={handleOpen}
            placeholder={props.placeholder}
            label={props.label}
            inputRef={anchorRef}
            sx={{ cursor: 'pointer' }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <ArrowDropDownRoundedIcon
                      sx={{
                        transition: 'transform 0.3s ease',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </InputAdornment>
                ),
              },
              inputLabel: { shrink: true },
            }}
          />
        </FormControl>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              { invalidDate && (
                <Box sx={{ my: 2 }}>
                  <ErrorBox errorMessage='Não é permitido data final anterior a data inicial' onClose={handleCancel} />
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="center">
                <DatePicker
                  label="Data inicial"
                  value={tempStartDate}
                  onChange={(newDate) => setTempStartDate(newDate)}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
                <Box>–</Box>
                <DatePicker
                  label="Data final"
                  value={tempEndDate}
                  onChange={(newDate) => setTempEndDate(newDate)}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
              </Box>

              <Box display="flex" gap={0} justifyContent="center" flexWrap="wrap" sx={{ py: 2 }}>
                <StaticDatePicker
                  sx={{ flex: 1, minWidth: '200px', border: '1px solid #0000001F' }}
                  displayStaticWrapperAs="desktop"
                  value={tempStartDate}
                  onChange={(newValue) => setTempStartDate(newValue)}
                  slotProps={{
                    actionBar: { actions: undefined },
                  }}
                />
                <StaticDatePicker
                  sx={{ flex: 1, minWidth: '200px', border: '1px solid #0000001F' }}
                  displayStaticWrapperAs="desktop"
                  value={tempEndDate}
                  onChange={(newValue) => setTempEndDate(newValue)}
                  slotProps={{
                    actionBar: { actions: undefined },
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" pt={1}>
                <Typography variant="body2" color="text.secondary">
                  {displayValue || ''}
                </Typography>
                <Box>
                  <Button onClick={handleCancel} variant="transparent">
                    <Typography variant="subtitle2" color="text.primary">
                      Cancelar
                    </Typography>
                  </Button>
                  <Button onClick={handleConfirm} variant="transparent" disabled={invalidDate}>
                    <Typography variant="subtitle2" color={invalidDate ? "text.disabled" : "primary"}>
                      Continuar
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    </LocalizationProvider>
  );
};

export default DataFilter;
